import { FC, ReactNode } from "react";
import clsx from "clsx";
import * as Toast from "@radix-ui/react-toast";
import { Icon } from "react-migration/components/Icon";

export enum NotificationType {
  success = "success",
  error = "error",
  warning = "warning",
  info = "info",
}

export type NotificationProps = {
  id: string;
  title: string;
  description?: string | ReactNode;
  type?: keyof typeof NotificationType;
  duration?: number;
  closable?: boolean;
  onClose?: () => void;
  className?: string;
  actions?: ReactNode;
};

const notificationTypeStyles = {
  success: {
    background: "bg-green-100",
    progressBar: "bg-green-200",
    progressBarFill: "bg-green-300",
  },
  error: {
    background: "bg-red-100",
    progressBar: "bg-red-200",
    progressBarFill: "bg-red-300",
  },
  warning: {
    background: "bg-amber-100",
    progressBar: "bg-amber-200",
    progressBarFill: "bg-amber-300",
  },
  info: {
    background: "bg-platform-blue-100",
    progressBar: "bg-platform-blue-200",
    progressBarFill: "bg-platform-blue-300",
  },
};

export const Notification: FC<NotificationProps> = ({
  id,
  title,
  description,
  type,
  duration = 5000,
  closable = false,
  onClose,
  className,
  actions,
}) => {
  const notificationTypeStyle = type && notificationTypeStyles[type];

  return (
    <>
      <style>
        {`
          @keyframes grow {
            0% { transform: scaleX(0); }
            100% { transform: scaleX(1); }
          }
        `}
      </style>
      <Toast.Root
        key={id}
        className={clsx(
          "rounded-lg shadow-lg overflow-hidden",
          "max-w-xs w-full",
          "data-[state=open]:animate-slideIn",
          "data-[state=closed]:animate-hide",
          "data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x)",
          "data-[swipe=cancel]:translate-x-0",
          "data-[swipe=end]:animate-swipeOut",
          notificationTypeStyle?.background || "bg-light",
          className
        )}
        duration={duration}
        onOpenChange={(open) => {
          if (!open && onClose) onClose();
        }}
        data-testid="li-react-notification"
      >
        <div className="p-4">
          <div className="flex items-center justify-between gap-1">
            <Toast.Title className="text-sm font-medium">{title}</Toast.Title>
            {closable && (
              <Toast.Close className="p-1 rounded-sm hover:bg-gray-100">
                <Icon size="sm" icon="icon-lt-close-line text-neutral-500" extraClasses="pointer-events-none" />
              </Toast.Close>
            )}
          </div>

          {description && (
            <Toast.Description className="text-xs text-blue-grey-950/50">{description}</Toast.Description>
          )}

          {actions && <div className="flex gap-2 mt-2">{actions}</div>}
        </div>

        <div className={clsx("w-full h-1", notificationTypeStyle?.progressBar || "bg-blue-grey-400")}>
          <div
            className={clsx("h-full transform origin-left", notificationTypeStyle?.progressBarFill || "bg-dark")}
            style={{
              animation: `grow ${duration}ms linear forwards`,
            }}
          ></div>
        </div>
      </Toast.Root>
    </>
  );
};

// Export Toast.Action for convenience
export const NotificationAction = Toast.Action;
